export type GrantOpportunityMilestoneStage = "preparing" | "submitted" | "awarded";

export type GrantOpportunityMilestone = {
  id: number;
  grant_opportunity_id: number;
  title: string;
  stage: GrantOpportunityMilestoneStage;
  owner_id: number | null;
  owner_name: string | null;
  position: number;
  task_count: number;
  completed_task_count: number;
  created_at: string;
  updated_at: string;
};

const jsonHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

function milestonesPath(municipalityId: number, opportunityId: number): string {
  return `/api/v1/municipalities/${municipalityId}/grant_opportunities/${opportunityId}/milestones`;
}

export async function listGrantOpportunityMilestones(
  municipalityId: number,
  opportunityId: number,
): Promise<GrantOpportunityMilestone[]> {
  const response = await fetch(milestonesPath(municipalityId, opportunityId), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error("Failed to load milestones");
  }
  return response.json();
}

export async function createGrantOpportunityMilestone(
  municipalityId: number,
  opportunityId: number,
  payload: {
    title: string;
    stage?: GrantOpportunityMilestoneStage;
    owner_id?: number | null;
  },
): Promise<GrantOpportunityMilestone> {
  const response = await fetch(milestonesPath(municipalityId, opportunityId), {
    method: "POST",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ grant_opportunity_milestone: payload }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.errors?.join(", ") ?? "Failed to create milestone");
  }
  return response.json();
}

export async function updateGrantOpportunityMilestone(
  municipalityId: number,
  opportunityId: number,
  milestoneId: number,
  payload: Partial<Pick<GrantOpportunityMilestone, "title" | "stage" | "owner_id" | "position">>,
): Promise<GrantOpportunityMilestone> {
  const response = await fetch(`${milestonesPath(municipalityId, opportunityId)}/${milestoneId}`, {
    method: "PATCH",
    credentials: "include",
    headers: { ...jsonHeaders, "X-CSRF-Token": csrfToken() },
    body: JSON.stringify({ grant_opportunity_milestone: payload }),
  });
  if (!response.ok) {
    throw new Error("Failed to update milestone");
  }
  return response.json();
}

export async function deleteGrantOpportunityMilestone(
  municipalityId: number,
  opportunityId: number,
  milestoneId: number,
): Promise<void> {
  const response = await fetch(`${milestonesPath(municipalityId, opportunityId)}/${milestoneId}`, {
    method: "DELETE",
    credentials: "include",
    headers: { "X-CSRF-Token": csrfToken() },
  });
  if (!response.ok) {
    throw new Error("Failed to delete milestone");
  }
}
